/**
 * BoundaryPanel · KML/KMZ/SHP/GeoJSON upload + preview + version history + approve.
 */
import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  UploadSimple, CheckCircle, ClockCounterClockwise, CircleNotch,
  ArrowClockwise, MapTrifold,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BoundaryPanel({ pid, boundary, refetch }) {
  const [uploading, setUploading] = useState(false);
  const [crsCheck, setCrsCheck] = useState(null);      // last CRS-check payload
  const [pendingFile, setPendingFile] = useState(null); // file awaiting approval

  const versionsQ = useQuery({
    queryKey: ["boundary-versions", pid],
    queryFn: () => api.get(`/projects/${pid}/gis/boundary/versions`).then((r) => r.data),
  });

  const crsCheckMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${pid}/gis/boundary/crs-check`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      return r.data;
    },
  });

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${pid}/gis/boundary/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => {
      toast.success("Boundary uploaded — preview updated.");
      refetch(); versionsQ.refetch();
      setCrsCheck(null); setPendingFile(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const applyCorrectedMut = useMutation({
    mutationFn: async ({ file, source_epsg, kind }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("approved_source_epsg", String(source_epsg));
      fd.append("transformation_kind", kind);
      const r = await api.post(`/projects/${pid}/gis/boundary/apply-corrected`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => {
      toast.success("Corrected boundary saved (original preserved).");
      refetch(); versionsQ.refetch();
      setCrsCheck(null); setPendingFile(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Apply correction failed"),
  });

  const approveMut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/gis/boundary/approve`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Boundary approved as Project Boundary.");
      refetch(); versionsQ.refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Approve failed"),
  });

  const restoreMut = useMutation({
    mutationFn: (v) => api.post(`/projects/${pid}/gis/boundary/restore/${v}`).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Restored as new version ${r.new_version}`);
      refetch(); versionsQ.refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Restore failed"),
  });

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const report = await crsCheckMut.mutateAsync(file);
      setCrsCheck(report); setPendingFile(file);
      // If confidence is high AND the file is already in a form we can
      // save directly (WGS84 lon/lat), let the user proceed to the
      // classic upload flow with a single click below.
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CRS check failed");
    } finally {
      setUploading(false); e.target.value = "";
    }
  }, [crsCheckMut]);

  const versions = versionsQ.data?.versions || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="boundary-panel">
      {/* Upload card */}
      <section className="lg:col-span-2 rounded-lg bg-white border border-slate-200 p-5" data-testid="boundary-upload-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            <MapTrifold size={16} className="text-brand-blue" weight="duotone" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-[15px] text-slate-900">Project Boundary</h2>
            <p className="text-[11.5px] text-slate-500">Upload KML, KMZ, GeoJSON or ESRI Shapefile ZIP. Boundary must be in WGS-84 (EPSG:4326).</p>
          </div>
          {boundary?.approved && (
            <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10.5px] uppercase tracking-widest">
              <CheckCircle size={10} weight="fill" /> Approved
            </span>
          )}
        </div>

        <label className="block mb-4">
          <input type="file"
                    accept=".kml,.kmz,.geojson,.json,.zip"
                    onChange={onFile}
                    disabled={uploading}
                    data-testid="input-boundary-upload"
                    className="w-full text-[12px] file:mr-3 file:h-10 file:rounded-md file:border-0 file:bg-brand-blue file:text-white file:px-3 file:text-[11.5px] file:font-semibold hover:file:bg-brand-blue/90" />
        </label>
        {uploading && (
          <div className="text-[11.5px] text-slate-500 flex items-center gap-1 mb-3">
            <CircleNotch size={11} className="animate-spin" /> Uploading & parsing…
          </div>
        )}

        {/* Feb 2026 · CRS & Boundary Check — non-destructive validation */}
        {crsCheck && pendingFile && (
          <div className="space-y-4 mb-4" data-testid="crs-check-wrapper">

            {/* Iter81 · Prominent Detection Banner ─────────────────── */}
            {(() => {
              const conf = Number(crsCheck.confidence_overall || 0);
              const inside = !!crsCheck.alignment?.inside_country;
              const totalBoreholes = Number(crsCheck.drillhole_count || 0);
              const bhOut = Number(crsCheck.alignment?.boreholes_outside || 0);
              const usedTransform = (crsCheck.transformations || [])
                    .find(t => t.kind === "swap_xy" || t.kind === "reproject");
              // Normalise country display — backend may return ISO codes
              // for legacy projects.
              const _countryMap = {
                TZ: "Tanzania", ZM: "Zambia", KE: "Kenya", UG: "Uganda",
                ZW: "Zimbabwe", ZA: "South Africa", GH: "Ghana",
                ML: "Mali", NG: "Nigeria", ET: "Ethiopia", CD: "DRC",
                CG: "Republic of Congo", MA: "Morocco", DZ: "Algeria",
                AU: "Australia", CA: "Canada", US: "USA", CL: "Chile",
                PE: "Peru", BR: "Brazil", IN: "India", CN: "China",
                MN: "Mongolia", KZ: "Kazakhstan", AF: "Afghanistan",
                PG: "Papua New Guinea", RW: "Rwanda", BI: "Burundi",
                SD: "Sudan", SS: "South Sudan", CI: "Ivory Coast",
                BF: "Burkina Faso", SN: "Senegal", GN: "Guinea",
                LR: "Liberia", SL: "Sierra Leone", GA: "Gabon",
                CM: "Cameroon", MR: "Mauritania", BW: "Botswana",
                NA: "Namibia", MZ: "Mozambique", MW: "Malawi",
              };
              const raw = crsCheck.project_country || crsCheck.country || "";
              const countryDisplay = _countryMap[String(raw).toUpperCase()]
                                              || String(raw).replace(/^./, c => c.toUpperCase());
              let status;
              if (conf >= 0.85 && inside) {
                status = { key: "detected", label: "CRS Detected · Validation Passed", tone: "emerald" };
              } else if (conf >= 0.75) {
                status = { key: "passed", label: "Validation Passed", tone: "sky" };
              } else if (usedTransform && conf >= 0.5) {
                status = { key: "corrected", label: "Automatically Corrected", tone: "amber" };
              } else {
                status = { key: "manual", label: "Manual Review Required", tone: "rose" };
              }
              const bannerCls = {
                emerald: "border-emerald-300 bg-gradient-to-br from-emerald-50 to-white",
                sky:     "border-sky-300 bg-gradient-to-br from-sky-50 to-white",
                amber:   "border-amber-300 bg-gradient-to-br from-amber-50 to-white",
                rose:    "border-rose-300 bg-gradient-to-br from-rose-50 to-white",
              }[status.tone];
              const badgeCls = {
                emerald: "bg-emerald-600",
                sky:     "bg-sky-600",
                amber:   "bg-amber-600",
                rose:    "bg-rose-600",
              }[status.tone];
              const textAccent = {
                emerald: "text-emerald-900",
                sky:     "text-sky-900",
                amber:   "text-amber-900",
                rose:    "text-rose-900",
              }[status.tone];
              return (
                <div className={`rounded-lg border-2 p-4 ${bannerCls}`}
                          data-testid="crs-detection-banner"
                          data-status={status.key}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-11 h-11 rounded-full ${badgeCls} flex items-center justify-center text-white`}>
                      <MapTrifold size={20} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded-full ${badgeCls} text-white px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold`}
                                  data-testid="crs-status-badge">
                          <CheckCircle size={10} weight="fill" /> {status.label}
                        </span>
                        <span className={`text-[10.5px] uppercase tracking-widest ${textAccent}`}>
                          Confidence: <b className="font-mono" data-testid="crs-banner-confidence">
                            {Math.round(conf * 100)}%
                          </b>
                        </span>
                      </div>
                      <div className={`mt-1 font-heading font-black text-[18px] tracking-tight ${textAccent}`}>
                        {status.label}
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                        <BannerCell label="Country" value={countryDisplay || "—"}
                                       testId="crs-banner-country" tone={status.tone} />
                        <BannerCell label="Datum"
                                       value={crsCheck.recommended_project_crs?.datum || "—"}
                                       testId="crs-banner-datum" tone={status.tone} mono />
                        <BannerCell label="UTM Zone"
                                       value={crsCheck.recommended_project_crs?.utm_zone || "—"}
                                       testId="crs-banner-utm" tone={status.tone} mono />
                        <BannerCell label="EPSG"
                                       value={crsCheck.recommended_project_crs?.recommended_epsg
                                                 ? `EPSG:${crsCheck.recommended_project_crs.recommended_epsg}` : "—"}
                                       testId="crs-banner-epsg" tone={status.tone} mono />
                      </div>
                      {totalBoreholes > 0 && (
                        <div className={`mt-2 text-[11px] ${textAccent}`}>
                          Boreholes in scope: <b>{totalBoreholes}</b> ·
                          Inside boundary: <b data-testid="crs-banner-bh-inside">
                            {crsCheck.alignment?.boreholes_inside ?? 0}
                          </b> ·
                          Outside: <b data-testid="crs-banner-bh-outside">{bhOut}</b>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="crs-check-card">
            <div className="flex items-start gap-2">
              <MapTrifold size={16} className="text-amber-700 mt-0.5" weight="duotone" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-heading font-bold text-slate-900">CRS &amp; Boundary Check</div>
                <div className="text-[11px] text-slate-600">
                  Detected source: <b className="font-mono">
                    {crsCheck.detected_source?.epsg ? `EPSG:${crsCheck.detected_source.epsg}` : "unknown"}
                  </b> · Confidence:{" "}
                  <b className={crsCheck.confidence_overall >= 0.75 ? "text-emerald-700" : "text-amber-800"}>
                    {(crsCheck.confidence_overall * 100).toFixed(0)}%
                  </b>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10.5px]">
              <Field label="Country" value={crsCheck.project_country || "—"} />
              <Field label="UTM Zone" value={crsCheck.recommended_project_crs?.utm_zone || "—"} mono />
              <Field label="Datum" value={crsCheck.recommended_project_crs?.datum || "—"} />
              <Field label="Recommended CRS"
                        value={`EPSG:${crsCheck.recommended_project_crs?.recommended_epsg}`} mono />
              <Field label="Inside country"
                        value={crsCheck.alignment?.inside_country ? "Yes ✓" : "No ✗"} />
              <Field label="Boreholes inside"
                        value={crsCheck.alignment?.boreholes_inside ?? "—"} mono />
              <Field label="Boreholes outside"
                        value={crsCheck.alignment?.boreholes_outside ?? "—"} mono />
              <Field label="Drillholes checked"
                        value={crsCheck.drillhole_count ?? 0} mono />
            </div>

            {(crsCheck.detected_source?.warnings || []).length > 0 && (
              <div className="rounded-md bg-white border border-amber-200 p-2 text-[11px] text-amber-900 space-y-0.5">
                <div className="font-semibold">Warnings</div>
                {crsCheck.detected_source.warnings.map((w, i) => (
                  <div key={i}>• {w}</div>
                ))}
              </div>
            )}

            {crsCheck.approval_needed_reason && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-[11px] text-rose-800">
                {crsCheck.approval_needed_reason}
              </div>
            )}

            <div className="space-y-1">
              <div className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">Candidate transformations</div>
              {crsCheck.transformations?.length === 0 && (
                <div className="text-[11px] text-slate-500 italic">
                  No candidate transformations — please select a source CRS manually.
                </div>
              )}
              {(crsCheck.transformations || []).map((t, i) => (
                <div key={i} className="rounded border border-slate-200 bg-white p-2 text-[11px] flex items-center gap-3" data-testid={`crs-transform-${t.kind}-${t.source_epsg}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{t.label}</div>
                    <div className="text-[10.5px] text-slate-500">
                      Centroid → ({t.centroid_wgs84.lon.toFixed(4)}°, {t.centroid_wgs84.lat.toFixed(4)}°)
                      · Inside country: <b className={t.inside_country ? "text-emerald-700" : "text-rose-700"}>{t.inside_country ? "Yes" : "No"}</b>
                      {t.boreholes_outside !== undefined && (
                        <> · Boreholes inside: <b>{t.boreholes_inside}</b> · outside: <b>{t.boreholes_outside}</b></>
                      )}
                    </div>
                  </div>
                  <Button size="sm"
                              onClick={() => applyCorrectedMut.mutate({ file: pendingFile, source_epsg: t.source_epsg, kind: t.kind })}
                              disabled={applyCorrectedMut.isPending}
                              data-testid={`btn-approve-crs-${t.kind}-${t.source_epsg}`}
                              className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11px]">
                    Approve &amp; Save
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-1 border-t border-amber-200">
              <Button size="sm" variant="outline" onClick={() => uploadMut.mutate(pendingFile)}
                          disabled={uploadMut.isPending}
                          data-testid="btn-keep-original-boundary"
                          className="text-[11px]">
                Keep Original (skip correction)
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setCrsCheck(null); setPendingFile(null); }}
                          data-testid="btn-cancel-crs-check"
                          className="text-[11px] text-slate-500">
                Cancel
              </Button>
              <div className="ml-auto text-[10px] text-slate-500 italic">
                Original file is always preserved · corrections save as a new version.
              </div>
            </div>
          </div>
          </div>
        )}

        {boundary && !boundary.boundary && !boundary.filename && (
          <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/60 px-3 py-6 text-center text-[11.5px] text-slate-500">
            No boundary uploaded yet.
          </div>
        )}

        {boundary?.filename && (
          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="boundary-preview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
              <Field label="File"        value={boundary.filename} />
              <Field label="Area"        value={boundary.area_km2 ? `${Number(boundary.area_km2).toFixed(2)} km²` : "—"} mono />
              <Field label="Perimeter"   value={boundary.perimeter_km ? `${Number(boundary.perimeter_km).toFixed(2)} km` : "—"} mono />
              <Field label="UTM"          value={boundary.utm_zone ? `${boundary.utm_zone}${boundary.hemisphere || ""} (EPSG:${boundary.epsg_utm})` : "—"} mono />
              <Field label="Centroid"    value={boundary.centroid ? `${boundary.centroid[1].toFixed(4)}°, ${boundary.centroid[0].toFixed(4)}°` : "—"} mono />
              <Field label="Source CRS" value={boundary.source_crs || "—"} mono />
              <Field label="Uploaded"    value={boundary.uploaded_at ? new Date(boundary.uploaded_at).toLocaleString() : "—"} />
              <Field label="Version"      value={`v${boundary.version || 1}`} mono />
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
              <Button size="sm" onClick={() => approveMut.mutate()}
                          disabled={boundary.approved || approveMut.isPending}
                          data-testid="btn-approve-boundary"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11.5px]">
                <CheckCircle size={12} className="mr-1" weight="fill" />
                {boundary.approved ? "Approved" : "Approve as Project Boundary"}
              </Button>
              <span className="text-[10.5px] text-slate-500">
                Approving a boundary locks it into every downstream module (GIS maps, block model, CPR).
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Version history */}
      <section className="rounded-lg bg-white border border-slate-200 p-5" data-testid="boundary-versions-card">
        <div className="flex items-center gap-3 mb-3">
          <ClockCounterClockwise size={16} className="text-slate-500" />
          <h2 className="font-heading font-bold text-[15px] text-slate-900">Version History</h2>
          <button type="button" onClick={() => versionsQ.refetch()}
                     data-testid="btn-refresh-versions"
                     className="ml-auto text-[11px] text-slate-500 hover:text-brand-blue">
            <ArrowClockwise size={11} className={versionsQ.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="space-y-2" data-testid="boundary-version-list">
          {versions.length === 0 && (
            <div className="text-[11.5px] text-slate-500 italic">No versions yet — upload a boundary to start.</div>
          )}
          {versions.map((v) => (
            <div key={v.version}
                    data-testid={`boundary-version-${v.version}`}
                    className={`flex items-start gap-2 border rounded-md p-2 text-[11px]
                                    ${v.approved ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50/40"}`}>
              <div className="w-7 shrink-0 text-center">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">V</div>
                <div className="font-mono font-bold text-brand-blue">{v.version}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 truncate">{v.filename || "boundary"}</div>
                <div className="text-slate-500 text-[10.5px]">
                  {v.area_km2 ? `${Number(v.area_km2).toFixed(2)} km² · ` : ""}
                  {v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : "?"}
                </div>
                {v.restored_from && (
                  <div className="text-[9.5px] text-amber-700 italic">restored from v{v.restored_from}</div>
                )}
              </div>
              {v.approved && (
                <CheckCircle size={12} className="text-emerald-600 shrink-0" weight="fill" />
              )}
              {!v.approved && v.version !== (boundary?.version || -1) && (
                <button type="button"
                            onClick={() => restoreMut.mutate(v.version)}
                            data-testid={`btn-restore-v${v.version}`}
                            className="text-[10.5px] text-brand-blue hover:underline shrink-0">
                  Restore
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-[11.5px] text-slate-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function BannerCell({ label, value, tone = "emerald", mono, testId }) {
  const cls = {
    emerald: "border-emerald-200 bg-white/70 text-emerald-900",
    sky:     "border-sky-200 bg-white/70 text-sky-900",
    amber:   "border-amber-200 bg-white/70 text-amber-900",
    rose:    "border-rose-200 bg-white/70 text-rose-900",
  }[tone] || "border-slate-200 bg-white text-slate-900";
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${cls}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className={`text-[13px] font-bold ${mono ? "font-mono" : ""} truncate`}
              title={String(value ?? "")}>
        {value ?? "—"}
      </div>
    </div>
  );
}
